import { type ReactElement, useEffect, useState } from 'react';

import Button from '../Button';
import List from '../List';

import { EditableMultiInputRow } from './EditableMultiInputRow';

import styles from './EditableMultiInputContainer.module.less';

const DEFAULT_INITIAL_LOAD_SIZE = 5;
const DEFAULT_LOAD_MORE_SIZE = 5;

export type EditableMultiInputItem = {
  id: number;
  name: string;
  /**
   * Whether the item is newly created and should not be editable.
   */
  isNew?: boolean;
};

export type Props<T extends EditableMultiInputItem> = {
  items: T[];
  /**
   * The function to call when an item is deleted. This should take the remaining items and perform some action with them.
   * @example
   * const [packageLabels, setPackageLabels] = useState<item[]>([]);
   * onItemDelete={(items) => {
   *   setPackageLabels(items);
   * }}
   */
  onItemDelete: (items: T[]) => void;
  /**
   * The function to call when an item is edited. This should take the updated items and perform some action with them.
   * @example
   * const [packageLabels, setPackageLabels] = useState<item[]>([]);
   * onItemEdit={(items) => {
   *   setPackageLabels(items);
   * }}
   */
  onItemEdit: (editedItem: T, newName: string, updatedItems: T[]) => void;
  /**
   * The initial number of items to load.
   */
  initialLoadSize?: number;
  /**
   * The number of items to load when the user clicks the "Load more" button.
   */
  loadMoreSize?: number;
  /**
   * an optional search term to filter the items by.
   */
  searchTerm?: string;
  /**
   * a function to return the body of the dialog that is shown when the user edits an item.
   * whether the user has permission to edit and delete the items.
   */
  hasEditPermission: boolean;
  /**
   * the maximum length of the item name used to validate the input.
   */
  maxItemNameLength: number;
  /**
   * Optional regex to validate the input.
   */
  inputValidationRegex?: RegExp;
  /**
   * Optional callback when validation state changes.
   */
  onValidationChange?: (isValid: boolean) => void;
};

/**
 * Component that renders a list of editable items.
 * This is a modernized version of the MultiTextInput component that supports editing and deleting items.
 * While any onItemDelete and onItemEdit functions can be provided, this component does not handle updating the overall items state
 * so those functions should be ready to handle it. See the prop examples for more details.
 *
 * By default, only a few items are loaded and the rest are loaded on demand, but note that
 * this assumes that the calling code will handle the pre-loading of the items, rather than dynamical or paginated loading.
 * This component is intended to be used in conjunction with a search bar that updates the searchTerm state.
 */
export const EditableMultiInputContainer = <T extends EditableMultiInputItem>({
  items,
  onItemDelete,
  onItemEdit,
  initialLoadSize = DEFAULT_INITIAL_LOAD_SIZE,
  loadMoreSize = DEFAULT_LOAD_MORE_SIZE,
  searchTerm,
  hasEditPermission,
  maxItemNameLength,
  inputValidationRegex,
  onValidationChange,
}: Props<T>): ReactElement => {
  const sortedItems = items.sort((a, b) => a.name.localeCompare(b.name));
  const [loadSize, setLoadSize] = useState(initialLoadSize);

  const [loadedItems, setLoadedItems] = useState<T[]>(sortedItems.slice(0, loadSize));
  const [shouldShowLoadMore, setShouldShowLoadMore] = useState(loadedItems.length < items.length);
  const [isEditing, setIsEditing] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      setLoadedItems(items.slice(0, 500));
      setShouldShowLoadMore(false);
    } else {
      setLoadedItems(sortedItems.slice(0, loadSize));
      setShouldShowLoadMore(loadSize < sortedItems.length);
    }
    // intentionally not using exhaustive-deps because
    // this shouldn't re-run when the loadedItems size changes or we'll hit an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loadSize, searchTerm]);

  const onLoadMore = () => {
    const newSize = loadedItems.length + loadMoreSize;
    setLoadedItems(sortedItems.slice(0, newSize));
    setShouldShowLoadMore(newSize < sortedItems.length);
    setLoadSize(newSize);
  };

  const toggleIsEditing = (itemId: number) => {
    setIsEditing((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleValidationChange = (isValid: boolean) => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  };

  const filteredItems = loadedItems.filter(
    (item) => !searchTerm || item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div>
      <List isBorderless>
        {filteredItems.map((item, idx) => (
          <EditableMultiInputRow
            key={`${item.id}-${item.name}`}
            item={item}
            isEditing={!!isEditing[item.id]}
            toggleIsEditing={() => toggleIsEditing(item.id)}
            onDelete={() => {
              const newItems = items.filter((existingItem) => existingItem.id !== item.id);
              onItemDelete(newItems);
            }}
            onEdit={(newName: string) => {
              const newItems = items.map((existingItem) =>
                existingItem.id === item.id ? { ...existingItem, name: newName } : existingItem,
              );
              onItemEdit(item, newName, newItems);
            }}
            index={idx}
            hasEditPermission={hasEditPermission}
            maxItemNameLength={maxItemNameLength}
            inputValidationRegex={inputValidationRegex}
            onValidationChange={(isValid) => handleValidationChange(isValid)}
          />
        ))}
      </List>
      {shouldShowLoadMore && (
        <div className={styles.loadMore}>
          <Button size="medium" type="secondary" onClick={onLoadMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};
